import { type SupabaseClient } from "@supabase/supabase-js";
import type { AppState, ProviderStatus } from "../../shared/types";
export interface Identity {
  id?: string;
  storage: "local" | "supabase" | "cloudflare";
  email: string | null;
  anonymous: boolean;
}
export interface ApiResponse {
  state: AppState;
  identity?: Identity;
  provider?: ProviderStatus;
  results?: string[];
}
export let cloud: SupabaseClient | null = null;
let explicitCancellation = false;
export async function initializeCloud() {
  const res = await fetch("/api/config");
  if (!res.ok) throw new Error("The server could not be reached.");
  const config = await res.json();
  explicitCancellation = config.explicitCancellation === true;
  if (config.supabase) {
    const { createClient } = await import("@supabase/supabase-js");
    cloud = createClient(config.supabase.url, config.supabase.publishableKey, {
      // This callback belongs to the linked chess account. Do not let the
      // sign-in client consume its code/error or clear the Cretchess session.
      auth: { detectSessionInUrl: location.pathname !== "/lichess/callback" },
    });
  }
  return config as {
    provider: ProviderStatus;
    supabase: { url: string; publishableKey: string } | null;
  };
}
export async function authHeaders() {
  const session = cloud ? (await cloud.auth.getSession()).data.session : null;
  return session
    ? { Authorization: `Bearer ${session.access_token}` }
    : { Authorization: "" };
}
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
  }
}
export async function api(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
  applied?: (state: AppState) => void,
): Promise<ApiResponse> {
  const res = await fetch(`/api/${path}`, {
    method: body === undefined ? "GET" : "POST",
    headers: {
      "Content-Type": "application/json",
      ...(await authHeaders()),
      ...(applied ? { Accept: "application/x-ndjson" } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
  if (
    res.headers.get("Content-Type")?.includes("application/x-ndjson") &&
    res.body
  ) {
    const reader = res.body.getReader(),
      decoder = new TextDecoder();
    let buffer = "",
      result: ApiResponse | undefined;
    try {
      for (;;) {
        const { value, done } = await reader.read();
        buffer += decoder.decode(value, { stream: !done });
        const lines = buffer.split("\n");
        buffer = lines.pop()!;
        for (const line of lines) {
          if (!line.trim()) continue;
          const event = JSON.parse(line);
          if (event.type === "error")
            throw new ApiError(event.error, event.status ?? 503, event.code);
          if (event.type === "applied") applied?.(event.state);
          if (event.type === "saved") result = event.data;
        }
        if (done) break;
      }
    } finally {
      reader.releaseLock();
    }
    if (!result)
      throw new Error("The save connection closed before confirmation.");
    return result;
  }
  const data = await res.json();
  if (!res.ok)
    throw new ApiError(
      data.error ?? "The request failed.",
      res.status,
      data.code,
    );
  return data;
}
export async function download(path: string, filename: string) {
  const res = await fetch(`/api/${path}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error("The export could not be downloaded.");
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export async function cancelRequest(requestId: string | null): Promise<void> {
  if (requestId && explicitCancellation) await api("cancel", { requestId });
}
